// Constructive Solid Geometry (CSG) — boolean operations on triangle meshes.
//
// Implementation: BSP-tree approach by Evan Wallace (csg.js, MIT). Each mesh
// becomes a tree of polygons partitioned by their planes. Booleans are then
// expressed as combinations of "clip A by B" and invert operations:
//
//   A ∪ B  =  clip(A, B); clip(B, A); invert(B); clip(B, A); invert(B); merge
//   A − B  =  invert(A); clip(A, B); clip(B, A); invert(B); clip(B, A); invert(B); merge; invert(A)
//   A ∩ B  =  invert(A); clip(B, A); invert(B); clip(A, B); clip(B, A); merge; invert(A)
//
// Limitations:
// - Inputs MUST be closed manifolds (no holes, no inside-out faces).
//   Open surfaces give garbage — vase walls without a top cap will produce
//   weird artefacts.
// - O(n log n) typical, O(n²) worst case for highly non-convex inputs.
//   For ClayForge mesh sizes (hundreds to low thousands of triangles) this
//   runs in tens of milliseconds.
// - Coplanar coincident faces use an epsilon — extremely thin slivers may
//   classify wrong. EPSILON = 1e-5 works for mm-scale meshes.

import type { Triangle, Vec3 } from './types';

const EPSILON = 1e-5;

// ----- Vec3 helpers (plain tuples — same as ClayForge convention) -----

const sub  = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add  = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scl  = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const dot  = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0]
];
const norm = (a: Vec3): Vec3 => {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};
const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t
];

// ----- Vertex -----

interface Vertex { pos: Vec3; normal: Vec3 }
const vertexFlip = (v: Vertex): Vertex => ({ pos: v.pos, normal: scl(v.normal, -1) });
const vertexLerp = (a: Vertex, b: Vertex, t: number): Vertex => ({
  pos: lerp(a.pos, b.pos, t),
  normal: lerp(a.normal, b.normal, t)
});

// ----- Plane -----

interface Plane { normal: Vec3; w: number }
function planeFromPoints(a: Vec3, b: Vec3, c: Vec3): Plane {
  const n = norm(cross(sub(b, a), sub(c, a)));
  return { normal: n, w: dot(n, a) };
}
const planeFlip = (p: Plane): Plane => ({ normal: scl(p.normal, -1), w: -p.w });

// ----- Polygon -----

interface Polygon { vertices: Vertex[]; plane: Plane }

function polygonFromVertices(verts: Vertex[]): Polygon {
  return {
    vertices: verts,
    plane: planeFromPoints(verts[0].pos, verts[1].pos, verts[2].pos)
  };
}

function polygonFlip(p: Polygon): Polygon {
  return {
    vertices: p.vertices.slice().reverse().map(vertexFlip),
    plane: planeFlip(p.plane)
  };
}

// Classify polygon and split where it spans the plane. Pushes results into
// the four output buckets (any of them may be empty).
function splitPolygon(
  plane: Plane,
  polygon: Polygon,
  coplanarFront: Polygon[],
  coplanarBack: Polygon[],
  front: Polygon[],
  back: Polygon[]
): void {
  const COPLANAR = 0, FRONT = 1, BACK = 2, SPANNING = 3;
  let polygonType = 0;
  const types: number[] = [];
  for (const v of polygon.vertices) {
    const t = dot(plane.normal, v.pos) - plane.w;
    const type = (t < -EPSILON) ? BACK : (t > EPSILON) ? FRONT : COPLANAR;
    polygonType |= type;
    types.push(type);
  }

  switch (polygonType) {
    case COPLANAR:
      (dot(plane.normal, polygon.plane.normal) > 0 ? coplanarFront : coplanarBack).push(polygon);
      break;
    case FRONT:
      front.push(polygon);
      break;
    case BACK:
      back.push(polygon);
      break;
    case SPANNING: {
      const f: Vertex[] = [], b: Vertex[] = [];
      for (let i = 0; i < polygon.vertices.length; i++) {
        const j = (i + 1) % polygon.vertices.length;
        const ti = types[i], tj = types[j];
        const vi = polygon.vertices[i], vj = polygon.vertices[j];
        if (ti !== BACK) f.push(vi);
        if (ti !== FRONT) b.push(ti !== BACK ? { pos: vi.pos, normal: vi.normal } : vi);
        if ((ti | tj) === SPANNING) {
          const t = (plane.w - dot(plane.normal, vi.pos)) / dot(plane.normal, sub(vj.pos, vi.pos));
          const v = vertexLerp(vi, vj, t);
          f.push(v);
          b.push({ pos: v.pos, normal: v.normal });
        }
      }
      if (f.length >= 3) front.push(polygonFromVertices(f));
      if (b.length >= 3) back.push(polygonFromVertices(b));
      break;
    }
  }
}

// ----- BSP node -----

interface BSPNode {
  plane: Plane | null;
  front: BSPNode | null;
  back: BSPNode | null;
  polygons: Polygon[];
}

function newBSP(): BSPNode {
  return { plane: null, front: null, back: null, polygons: [] };
}

function bspBuild(node: BSPNode, polygons: Polygon[]): void {
  if (polygons.length === 0) return;
  if (!node.plane) node.plane = polygons[0].plane;
  const front: Polygon[] = [], back: Polygon[] = [];
  for (const p of polygons) {
    splitPolygon(node.plane, p, node.polygons, node.polygons, front, back);
  }
  if (front.length) {
    if (!node.front) node.front = newBSP();
    bspBuild(node.front, front);
  }
  if (back.length) {
    if (!node.back) node.back = newBSP();
    bspBuild(node.back, back);
  }
}

function bspInvert(node: BSPNode): void {
  for (const p of node.polygons) {
    const flipped = polygonFlip(p);
    p.vertices = flipped.vertices;
    p.plane = flipped.plane;
  }
  if (node.plane) node.plane = planeFlip(node.plane);
  const tmp = node.front;
  node.front = node.back;
  node.back = tmp;
  if (node.front) bspInvert(node.front);
  if (node.back) bspInvert(node.back);
}

function bspClipPolygons(node: BSPNode, polygons: Polygon[]): Polygon[] {
  if (!node.plane) return polygons.slice();
  let front: Polygon[] = [], back: Polygon[] = [];
  for (const p of polygons) {
    splitPolygon(node.plane, p, front, back, front, back);
  }
  if (node.front) front = bspClipPolygons(node.front, front);
  back = node.back ? bspClipPolygons(node.back, back) : [];
  return front.concat(back);
}

function bspClipTo(node: BSPNode, other: BSPNode): void {
  node.polygons = bspClipPolygons(other, node.polygons);
  if (node.front) bspClipTo(node.front, other);
  if (node.back) bspClipTo(node.back, other);
}

function bspAllPolygons(node: BSPNode): Polygon[] {
  let out = node.polygons.slice();
  if (node.front) out = out.concat(bspAllPolygons(node.front));
  if (node.back) out = out.concat(bspAllPolygons(node.back));
  return out;
}

// ----- Triangle ↔ Polygon adapters -----

function trianglesToPolygons(tris: Triangle[]): Polygon[] {
  const out: Polygon[] = [];
  for (const t of tris) {
    const verts: Vertex[] = [
      { pos: t.a, normal: t.normal },
      { pos: t.b, normal: t.normal },
      { pos: t.c, normal: t.normal }
    ];
    out.push(polygonFromVertices(verts));
  }
  return out;
}

function polygonsToTriangles(polys: Polygon[]): Triangle[] {
  const out: Triangle[] = [];
  for (const p of polys) {
    // Fan-triangulate the polygon (assumes convex — splits produce convex pieces).
    for (let i = 1; i < p.vertices.length - 1; i++) {
      const a = p.vertices[0].pos;
      const b = p.vertices[i].pos;
      const c = p.vertices[i + 1].pos;
      out.push({ a, b, c, normal: p.plane.normal });
    }
  }
  return out;
}

// ----- Public API -----

function bspFromTris(tris: Triangle[]): BSPNode {
  const node = newBSP();
  if (tris.length > 0) bspBuild(node, trianglesToPolygons(tris));
  return node;
}

export function csgUnion(a: Triangle[], b: Triangle[]): Triangle[] {
  if (a.length === 0) return b.slice();
  if (b.length === 0) return a.slice();
  const A = bspFromTris(a);
  const B = bspFromTris(b);
  bspClipTo(A, B);
  bspClipTo(B, A);
  bspInvert(B);
  bspClipTo(B, A);
  bspInvert(B);
  bspBuild(A, bspAllPolygons(B));
  return polygonsToTriangles(bspAllPolygons(A));
}

export function csgSubtract(a: Triangle[], b: Triangle[]): Triangle[] {
  if (a.length === 0) return [];
  if (b.length === 0) return a.slice();
  const A = bspFromTris(a);
  const B = bspFromTris(b);
  bspInvert(A);
  bspClipTo(A, B);
  bspClipTo(B, A);
  bspInvert(B);
  bspClipTo(B, A);
  bspInvert(B);
  bspBuild(A, bspAllPolygons(B));
  bspInvert(A);
  return polygonsToTriangles(bspAllPolygons(A));
}

export function csgIntersect(a: Triangle[], b: Triangle[]): Triangle[] {
  if (a.length === 0 || b.length === 0) return [];
  const A = bspFromTris(a);
  const B = bspFromTris(b);
  bspInvert(A);
  bspClipTo(B, A);
  bspInvert(B);
  bspClipTo(A, B);
  bspClipTo(B, A);
  bspBuild(A, bspAllPolygons(B));
  bspInvert(A);
  return polygonsToTriangles(bspAllPolygons(A));
}
